import _ from 'lodash';
import _isArray from 'lodash/isArray';
import _keys from 'lodash/keys';
import _pickBy from 'lodash/pickBy';
import { CategoryPotencyRanges } from 'shared/constants';
import { formatWeightOptions, getFormattedPotency, getPotencyValues, isPotencyProduct } from 'shared/helpers/products';
import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';

export { getFormattedPotency };

export function getPotencyStrings(product) {
  const potencyValues = getPotencyValues(product);
  const potencyStrings = getFormattedPotency(product);
  const showTHC = isPotencyProduct(product) && parseFloat(potencyValues.THC) > 0;
  const showCBD = isPotencyProduct(product) && parseFloat(potencyValues.CBD) > 0;
  const showTAC = isPotencyProduct(product) && parseFloat(potencyValues.TAC) > 0;
  return {
    THC: showTHC ? potencyStrings.THC : null,
    CBD: showCBD ? potencyStrings.CBD : null,
    TAC: showTAC ? potencyStrings.TAC : null,
  };
}

export function rangeIsValid(range, maxRange) {
  if (!range || _.some(range, (value) => !_.inRange(value, maxRange[0], maxRange[1] + 1))) {
    return false;
  }
  return true;
}

export function getPricesForMenuType({ product, menuType, returnSaleSpecialPrices = false }) {
  const { recPrices, medicalPrices, recSpecialPrices, medicalSpecialPrices } = product;
  const isMed = menuType === 'med';
  const prices = isMed ? medicalPrices : recPrices;
  const specialPrices = isMed ? medicalSpecialPrices : recSpecialPrices;

  return returnSaleSpecialPrices ? specialPrices : prices;
}

export function getOptionsQualifyingForOffer({ product, offerId }) {
  const matchingOffer = _.find(
    product.specialData?.bogoSpecials,
    ({ specialId, sourceId }) => specialId === offerId || sourceId === offerId
  );
  return matchingOffer?.qualifyingOptions || [];
}

/**
 * Groups product options with their corresponding prices, taking into account menu type and special offers
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.product - The product object containing options and pricing information
 * @param {string} params.menuType - The type of menu (e.g., 'MEDICAL', 'RECREATIONAL')
 * @param {boolean} params.isKiosk - Whether the menu is being displayed in kiosk mode
 * @param {Object} [params.saleSpecial] - Optional sale special to apply to pricing
 * @param {Object} [params.offerSpecial] - Optional offer special to apply to pricing
 * @param {string | undefined} [params.specialId] - Optional ID of the special to apply
 * @returns {Array<{ value: string, label: string, price: string }>} Array of objects containing all relevant info for each option: : [{ value: '1g', label: '1g', price: '10.00' }, { value: '1/8oz', label: '1/8oz', price: '40.00' }]
 */
export function groupOptionsWithPrices({ product, menuType, isKiosk, saleSpecial, offerSpecial, specialId }) {
  const {
    limitsPerCustomer,
    manualInventory,
    Options: options,
    optionsBelowKioskThreshold,
    optionsBelowThreshold,
    POSMetaData,
  } = product;
  const returnSaleSpecialPrices = _.isUndefined(saleSpecial)
    ? hasSaleSpecialForMenuType({ product, menuType })
    : saleSpecial;

  const prices = getPricesForMenuType({
    product,
    menuType,
    returnSaleSpecialPrices,
  });

  const qualifyingOptions = getOptionsQualifyingForOffer({ product, offerId: specialId });

  return formatWeightOptions({
    isKiosk,
    limitsPerCustomer,
    manualInventory,
    options,
    optionsBelowKioskThreshold,
    optionsBelowThreshold,
    prices,
    weightOnlyLabel: true,
    POSMetaData,
    showQualifyingOptions: !!offerSpecial,
    qualifyingOptions,
  });
}

const parsedProductsWeakMap = new WeakMap();
// this method is evil and should be 100% server side instead
export function parseProduct(product) {
  if (!product) {
    return product;
  }

  const cachedResult = parsedProductsWeakMap.get(product);
  if (cachedResult) {
    return cachedResult;
  }

  // These conversions are needed to support existing shape of product data.
  // Eventually these should be moved to the graphQl layer
  const { effects } = product;
  const parsedProduct = {
    ...product,
    // if object, filter out any key with a falsy value
    effects: _isArray(effects) ? effects : _keys(_pickBy(effects, (effect) => !!effect)),
    description: product.description || product.Description,
    name: product.name || product.Name,
    brand: product.brand || {},
  };

  parsedProductsWeakMap.set(product, parsedProduct);

  return parsedProduct;
}

export function potencyIsDefault(potencyRange = [], category) {
  const defaultRange = _.get(CategoryPotencyRanges, category?.key, []);
  return potencyRange[0] === defaultRange[0] && potencyRange[1] === defaultRange[1];
}

export const potencyWithinRange = (potencyContent, targetRange = []) => {
  if (!Array.isArray(targetRange) || targetRange.length !== 2) {
    return false;
  }

  const [min, max] = targetRange;

  if (!potencyContent) {
    if (min === 0) {
      return true;
    }
    return false;
  }

  // potencyContent is an array of a single value, or a two value range: verify:
  const overMin = potencyContent[0];
  const underMax = potencyContent[1] ?? potencyContent[0];

  return overMin >= min && underMax <= max;
};

export function cartItemId(item) {
  const additionalOption = item.additionalOption ? item.additionalOption : _.head(item.product?.AdditionalOptions);
  return `${item.product.id}-${item.option}${additionalOption ? `-${additionalOption}` : ''}`;
}
